/**
 * Brain Architecture - Complete Data Flow & Dependencies
 * Shows how everything connects, all variables, all calculations
 */

// ============================================================================
// INPUT VARIABLES - Everything that flows INTO the system
// ============================================================================
export const INPUT_VARIABLES = [
  {
    id: 'reviewText',
    name: 'Review Text',
    type: 'string',
    required: true,
    description: 'The actual review content from the customer',
    example: '"Das Essen war kalt und der Service langsam..."',
    flowsTo: ['detectLanguage', 'analyzeSentiment', 'analyzeAmbiguity', 'extractEntities'],
    category: 'core',
  },
  {
    id: 'businessName',
    name: 'Business Name',
    type: 'string',
    required: false,
    description: 'Name of the business (from settings or scraped)',
    example: '"Trattoria Milano"',
    flowsTo: ['buildIdentity', 'calculateContextScore'],
    category: 'context',
    priority: 1, // User settings > Scraped > Industry > Generic
  },
  {
    id: 'businessType',
    name: 'Business Type',
    type: 'enum',
    required: false,
    options: ['restaurant', 'hotel', 'dental', 'medical', 'realestate', 'homeservices', 'petservices', 'automotive', 'retail', 'fitness', 'salon', 'legal', 'daycare', 'coffeeshop', 'spa', 'photography', 'wedding', 'veterinarian', 'contractor'],
    description: 'Type of business - determines industry-specific patterns',
    flowsTo: ['getIndustryContext', 'getAmbiguityPatterns', 'getFewShotExamples', 'calculateContextScore'],
    category: 'context',
  },
  {
    id: 'businessContext',
    name: 'Business Context',
    type: 'string',
    required: false,
    description: 'Custom description of the business',
    example: '"Authentic Italian cuisine in Munich since 1985"',
    flowsTo: ['buildIdentity', 'calculateContextScore'],
    category: 'context',
  },
  {
    id: 'customInstructions',
    name: 'Custom Instructions',
    type: 'string',
    required: false,
    description: 'User-specific rules for responses',
    example: '"Always mention our homemade pasta"',
    flowsTo: ['buildIdentity', 'calculateContextScore'],
    category: 'context',
  },
  {
    id: 'tone',
    name: 'Tone',
    type: 'enum',
    required: false,
    options: ['professional', 'friendly', 'formal', 'apologetic'],
    default: 'professional',
    description: 'Communication style for the response',
    flowsTo: ['buildVoiceSection', 'shouldIncludeEmojis'],
    category: 'style',
  },
  {
    id: 'responseLength',
    name: 'Response Length',
    type: 'enum',
    required: false,
    options: ['short', 'medium', 'detailed', 'comprehensive'],
    default: 'auto',
    description: 'How long the response should be',
    flowsTo: ['calculateMaxTokens', 'buildOutputFormat'],
    category: 'style',
  },
  {
    id: 'includeBrackets',
    name: 'Include Brackets',
    type: 'boolean',
    required: false,
    default: true,
    description: 'Whether to include [options] for ambiguous parts',
    flowsTo: ['buildBracketSection', 'calculateMaxTokens'],
    category: 'style',
  },
  {
    id: 'includeEmojis',
    name: 'Include Emojis',
    type: 'boolean',
    required: false,
    default: 'auto',
    description: 'Whether to include emojis in response',
    flowsTo: ['buildVoiceSection'],
    category: 'style',
  },
  {
    id: 'includeSignature',
    name: 'Include Signature',
    type: 'boolean',
    required: false,
    default: true,
    description: 'Whether to sign off with business name',
    flowsTo: ['buildOutputFormat'],
    category: 'style',
  },
  {
    id: 'mode',
    name: 'Mode',
    type: 'enum',
    required: true,
    options: ['full', 'instant_try', 'demo', 'email', 'extension', 'bulk'],
    description: 'Context in which the response is generated',
    flowsTo: ['modeRouter'],
    category: 'system',
  },
];

// ============================================================================
// PROCESSING FUNCTIONS - What happens to the inputs
// ============================================================================
export const PROCESSING_FUNCTIONS = [
  {
    id: 'detectLanguage',
    name: 'Language Detection',
    icon: '🌍',
    description: 'Detects the language of the review (delegated to LLM)',
    inputs: ['reviewText'],
    outputs: ['languageHint'],
    calculation: 'LLM auto-detects from review context. Response written in same language.',
    code: `// V4.3.7: Removed regex detection, LLM handles it
// outputFormatSection includes:
"Language: AUTO-DETECT from review. Write response + bracket labels in same language."`,
  },
  {
    id: 'analyzeSentiment',
    name: 'Sentiment Analysis',
    icon: '😊😐😠',
    description: 'Analyzes the emotional tone and detects sarcasm',
    inputs: ['reviewText'],
    outputs: ['sentimentHint'],
    calculation: `Patterns checked:
- sarcastic_negative: "Great, 45 minutes..."
- conditional_negative: "used to be good"
- hedged_negative: "interesting..." (often means bad)`,
    code: `const SENTIMENT_PATTERNS = {
  sarcastic_negative: [
    /great[,.]?\\s*(just\\s*)?(\\d+|an hour|forever)/i,
    /love\\s*(it\\s+)?when/i,
    /thanks?\\s+for\\s+(nothing|the wait)/i,
  ],
  conditional_negative: [
    /used to be/i,
    /not (like|as good as) (before|it used to)/i,
  ]
};`,
  },
  {
    id: 'analyzeAmbiguity',
    name: 'Ambiguity Detection',
    icon: '🔍',
    description: 'Finds vague references that need user input',
    inputs: ['reviewText', 'businessType'],
    outputs: ['ambiguityScore', 'suggestedBrackets'],
    calculation: `Industry-specific patterns:
- Restaurant: "the food", "the waiter", "the dish"
- Hotel: "the room", "the pool", "reception"
- Dental: "the procedure", "the hygienist"

Score 0-100: 0 = fully specific, 100 = all ambiguous`,
    code: `const AMBIGUITY_PATTERNS = {
  restaurant: {
    vagueFood: /the (food|dish|meal|order)/i,
    vagueStaff: /the (waiter|waitress|server|staff)/i,
    vagueTiming: /the (wait|time|delay)/i,
  },
  hotel: {
    vagueRoom: /the (room|suite|accommodation)/i,
    vagueAmenity: /the (pool|gym|spa|breakfast)/i,
  }
};`,
  },
  {
    id: 'extractEntities',
    name: 'Entity Extraction',
    icon: '📦',
    description: 'Extracts specific mentions: dishes, staff, prices, times',
    inputs: ['reviewText', 'businessType'],
    outputs: ['reviewEntities'],
    calculation: `Extracts:
- dish: "the risotto", "pizza margherita"
- staff: "Marco", "the blonde waitress"
- wait_time: "45 minutes", "an hour"
- price: "€50", "too expensive"`,
    code: `const ENTITY_PATTERNS = {
  restaurant: {
    dish: /(?:the |my |our )?(\\w+(?:\\s+\\w+)?(?:pizza|pasta|steak|salad|soup))/i,
    wait_time: /(\\d+)\\s*(min|minutes?|hour|hours?)/i,
  }
};`,
  },
  {
    id: 'calculateContextScore',
    name: 'Context Score Calculation',
    icon: '📊',
    description: 'Measures how much context we have (0-100)',
    inputs: ['businessName', 'businessType', 'businessContext', 'customInstructions'],
    outputs: ['customizationScore'],
    calculation: `Simple scoring (25 points each):
- businessName: +25
- businessType: +25
- businessContext: +25
- customInstructions: +25

Total: 0-100`,
    code: `function calculateContextScore(context) {
  let score = 0;
  if (context.businessName) score += 25;
  if (context.businessType) score += 25;
  if (context.businessContext) score += 25;
  if (context.customInstructions) score += 25;
  return score;
}`,
  },
  {
    id: 'calculateBracketLevel',
    name: 'Bracket Level Calculation',
    icon: '⚡',
    description: 'Determines how many brackets to include',
    inputs: ['customizationScore', 'ambiguityScore', 'includeBrackets'],
    outputs: ['bracketLevel'],
    calculation: `Based on customizationScore:
- 0-24%: BUILDER MODE (90% brackets) - new user, no settings
- 25-49%: ASSISTED MODE (50/50)
- 50%+: CONTEXT MODE (minimal brackets)

If includeBrackets=false: 'none'`,
    code: `function calculateSmartBracketLevel(score, includeBrackets) {
  if (!includeBrackets) return 'none';
  if (score < 25) return 'ultra_aggressive';  // Builder mode
  if (score < 50) return 'aggressive';         // Assisted
  if (score < 75) return 'moderate';           // Context
  return 'conservative';                        // Full context
}`,
  },
  {
    id: 'calculateResponseLength',
    name: 'Response Length Calculation',
    icon: '📏',
    description: 'Determines optimal response length',
    inputs: ['reviewText', 'sentimentHint', 'responseLength'],
    outputs: ['effectiveLength', 'maxTokens'],
    calculation: `Auto-calculation considers:
- CRITICAL patterns: legal threats, health claims → longer
- SEVERE patterns: multiple complaints → longer
- Positive reviews → shorter
- User override always wins`,
    code: `const TOKEN_LIMITS = {
  short: { min: 50, target: 80, max: 120 },
  medium: { min: 80, target: 150, max: 200 },
  detailed: { min: 150, target: 250, max: 350 },
  comprehensive: { min: 250, target: 400, max: 500 }
};

const BRACKET_MULTIPLIER = 1.3; // +30% for brackets`,
  },
  {
    id: 'shouldIncludeEmojis',
    name: 'Emoji Decision',
    icon: '😀',
    description: 'Decides whether to include emojis',
    inputs: ['includeEmojis', 'tone', 'sentimentHint'],
    outputs: ['emojiGuidance'],
    calculation: `Priority:
1. User setting (if explicitly set)
2. Tone-based: friendly=yes, formal=minimal
3. Sentiment-based: negative=fewer, positive=more`,
    code: `function shouldIncludeEmojis(settings, tone, sentiment) {
  // User override wins
  if (settings.includeEmojis !== 'auto') {
    return settings.includeEmojis;
  }
  // Formal = no emojis
  if (tone === 'formal') return false;
  // Negative = no emojis
  if (sentiment === 'negative') return false;
  // Friendly + positive = emojis
  return tone === 'friendly';
}`,
  },
  {
    id: 'getUnifiedContext',
    name: 'Context Priority Merger',
    icon: '🔀',
    description: 'Merges context from multiple sources with priority',
    inputs: ['userSettings', 'scrapedData', 'industryDefaults'],
    outputs: ['unifiedContext'],
    calculation: `4-Level Priority Chain:
1. User Settings (highest) - what user typed in Settings
2. Scraped Data - auto-detected from website
3. Industry Defaults - based on businessType
4. Generic Fallback (lowest)

Higher priority fills gaps, doesn't override.`,
    code: `function getUnifiedContext({ userSettings, scrapedData, industryDefaults }) {
  return {
    ...GENERIC_DEFAULTS,
    ...industryDefaults[businessType],
    ...scrapedData,
    ...userSettings,  // Wins!
  };
}`,
  },
  {
    id: 'modeRouter',
    name: 'Mode Router',
    icon: '🔀',
    description: 'Applies mode-specific settings',
    inputs: ['mode'],
    outputs: ['modeSettings'],
    calculation: `Mode differences:
- full: User settings + brackets
- instant_try: Smart defaults + auto-tone
- demo: Smart defaults + auto-tone (for pre-gen)
- email: No brackets, auto-tone
- extension: User settings, no brackets
- bulk: User settings, no brackets`,
    code: `const MODE_SETTINGS = {
  full:        { brackets: 'user', tone: 'user' },
  instant_try: { brackets: 'smart', tone: 'user' },
  demo:        { brackets: 'smart', tone: 'auto' },
  email:       { brackets: 'off',   tone: 'auto' },
  extension:   { brackets: 'off',   tone: 'user' },
  bulk:        { brackets: 'off',   tone: 'user' },
};`,
  },
  {
    id: 'getIndustryContext',
    name: 'Industry Context Lookup',
    icon: '🏢',
    description: 'Retrieves industry-specific context, voice, and patterns',
    inputs: ['businessType'],
    outputs: ['industryContext', 'industryVoice', 'industryPatterns'],
    calculation: `19 industries available:
- restaurant, hotel, dental, medical, fitness, salon
- automotive, retail, ecommerce, veterinarian, contractor
- legal, realestate, daycare, coffeeshop, spa
- photography, wedding, generic (fallback)`,
    code: `const INDUSTRY_CONTEXTS = {
  restaurant: {
    context: 'As a restaurant owner you know...',
    voice: 'Warmth of someone who genuinely loves feeding people.',
  },
  hotel: {
    context: 'As a hotel owner you know...',
    voice: 'Professional hospitality.',
  },
  // ... 19 total industries
};`,
  },
  {
    id: 'filterSlopPhrases',
    name: 'AI Slop Filter',
    icon: '🚫',
    description: 'Blacklist of 70+ phrases and words that reveal AI content',
    inputs: ['voiceGuidance'],
    outputs: ['antiSlopInstructions'],
    calculation: `Banned patterns:
PHRASES: "Thank you for your feedback", "We appreciate", "Rest assured"
WORDS: thrilled, delighted, absolutely, definitely, firstly, furthermore

These are embedded in the voice_guidance section as NEVER rules.`,
    code: `const AI_SLOP_PHRASES = [
  'Thank you for your feedback',
  'We appreciate you taking the time',
  'Sorry for any inconvenience',
  // ... 30+ phrases
];

const AI_SLOP_WORDS = [
  'thrilled', 'delighted', 'ecstatic',
  'absolutely', 'definitely', 'certainly',
  // ... 40+ words
];`,
  },
  {
    id: 'calculateMaxTokens',
    name: 'Token Calculation',
    icon: '📏',
    description: 'Dynamic token limits based on review complexity',
    inputs: ['responseLength', 'includeBrackets', 'reviewText'],
    outputs: ['maxTokens', 'targetTokens'],
    calculation: `TOKEN_LIMITS by length:
- short: 50-120 tokens
- medium: 80-200 tokens
- detailed: 150-350 tokens
- comprehensive: 250-500 tokens

BRACKET_MULTIPLIER: 1.3x when brackets enabled
CRITICAL_PATTERNS: food poisoning, lawsuit → auto-comprehensive`,
    code: `const TOKEN_LIMITS = {
  short:         { min: 50,  target: 80,  max: 120 },
  medium:        { min: 80,  target: 150, max: 200 },
  detailed:      { min: 150, target: 250, max: 350 },
  comprehensive: { min: 250, target: 400, max: 500 }
};

const BRACKET_MULTIPLIER = 1.3;`,
  },
  {
    id: 'getAmbiguityPatterns',
    name: 'Ambiguity Pattern Lookup',
    icon: '🔎',
    description: '75+ industry-specific patterns for vague reference detection',
    inputs: ['businessType', 'reviewText'],
    outputs: ['ambiguityPatterns', 'matchedPatterns'],
    calculation: `Industry-specific patterns:
- Restaurant: "the food", "the waiter", "the dish"
- Hotel: "the room", "the pool", "reception"
- Dental: "the procedure", "the hygienist"

Each pattern has:
- regex: what to match
- excludeRegex: what makes it specific (not vague)
- bracketType: suggested bracket for this vagueness
- score: 5-15 points added to ambiguity score`,
    code: `const AMBIGUITY_PATTERNS = {
  restaurant: [
    {
      regex: /\\b(the food|das essen)\\b/i,
      excludeRegex: /\\b(pizza|pasta|steak)\\b/i,
      bracketType: 'dish_type',
      score: 15
    }
  ],
  // ... 15 industries, 75+ patterns
};`,
  },
];

// ============================================================================
// OUTPUT SECTIONS - What gets built for the LLM
// ============================================================================
export const OUTPUT_SECTIONS = [
  {
    id: 'identitySection',
    name: '<identity>',
    icon: '🎭',
    description: 'WHO you are - the business owner persona',
    builtFrom: ['businessName', 'businessType', 'businessContext', 'customInstructions'],
    content: `YOU ARE {BUSINESS_NAME}'S OWNER.
Your business is in {city}.

You built this {businessType}. You've seen thousands of customers.
One review doesn't shake you. You have context reviewers don't have.

SOVEREIGNTY PRINCIPLE: Acknowledge their EXPERIENCE, never their CONCLUSION.`,
  },
  {
    id: 'voiceSection',
    name: '<voice_guidance>',
    icon: '🎵',
    description: 'HOW you sound - tone and style rules',
    builtFrom: ['tone', 'emojiGuidance'],
    content: `TONE: {tone} - {toneDescription}
EMOJI: {emojiGuidance}

ANTI-SLOP: Never use corporate phrases like "Thank you for your feedback"
ANTI-ECHO: Never parrot the review back. Start fresh.`,
  },
  {
    id: 'responseApproach',
    name: '<response_approach>',
    icon: '🎯',
    description: 'WHAT to do based on sentiment',
    builtFrom: ['sentimentHint'],
    content: `Match the energy of the review:
- NEGATIVE/SEVERE: Acknowledge briefly, provide context, offer path forward
- MIXED: Appreciate positive, address concern. Don't be defensive.
- POSITIVE: Be genuinely happy! Reference what they loved. Invite back.`,
  },
  {
    id: 'bracketSection',
    name: '<brackets>',
    icon: '⚡',
    description: 'Smart bracket instructions',
    builtFrom: ['bracketLevel', 'sentimentHint', 'ambiguityScore'],
    content: `BRACKET LEVEL: {bracketLevel}

Format: [Label:Option1|Option2|Option3]
Strategic options based on sentiment:
- NEGATIVE: [Reaktion:Das tut mir leid|Lassen Sie mich erklären|Wir sehen das anders]
- POSITIVE: [Reaktion:Das freut uns riesig!|Danke!|Schön dass es geschmeckt hat]`,
  },
  {
    id: 'fewShotSection',
    name: '<examples>',
    icon: '📚',
    description: 'Curated examples for this context',
    builtFrom: ['businessType', 'sentimentHint'],
    content: `2-3 examples selected based on:
- Business type match
- Sentiment match
- Issue type match (if detected)

Database: 147+ examples across 9 business types × 5 sentiments`,
  },
  {
    id: 'outputFormat',
    name: '<output>',
    icon: '📤',
    description: 'FORMAT rules for the response',
    builtFrom: ['effectiveLength', 'includeSignature'],
    content: `LENGTH: {lengthInstruction}
SIGNATURE: {signatureInstruction}
LANGUAGE: AUTO-DETECT from review. Write in same language.`,
  },
];

// ============================================================================
// MODE COMPARISON - What's different in each mode
// ============================================================================
export const MODE_COMPARISON = {
  full: {
    name: 'Full Generator',
    description: 'Main generator for logged-in users',
    brackets: 'User setting (includeBrackets)',
    tone: 'User setting',
    context: 'Full user settings',
    usedBy: 'Generator page, Dashboard',
  },
  instant_try: {
    name: 'Instant Try',
    description: 'Quick demo on landing pages',
    brackets: 'Smart (based on ambiguity)',
    tone: 'User selectable',
    context: 'Minimal (just businessType)',
    usedBy: 'Landing page, Industry pages',
  },
  demo: {
    name: 'Demo Pre-Generation',
    description: 'Pre-generated demos for outreach',
    brackets: 'Smart (based on ambiguity)',
    tone: 'Auto-calculated (calculateAutoTone)',
    context: 'From scraped data',
    usedBy: 'Burst-4 Demo Generator',
  },
  email: {
    name: 'Email Mode',
    description: 'Responses embedded in emails',
    brackets: 'OFF',
    tone: 'Auto-calculated',
    context: 'From scraped data',
    usedBy: 'Demo emails, Follow-ups',
  },
  extension: {
    name: 'Chrome Extension',
    description: 'Browser extension responses',
    brackets: 'OFF',
    tone: 'User setting',
    context: 'User settings',
    usedBy: 'Chrome Extension',
  },
  bulk: {
    name: 'Bulk Generator',
    description: 'Process multiple reviews',
    brackets: 'OFF',
    tone: 'User setting',
    context: 'User settings',
    usedBy: 'Bulk Generator (Pro+)',
  },
};

// ============================================================================
// DEPENDENCY GRAPH - What depends on what
// ============================================================================
export const DEPENDENCIES = [
  // Input → Processing (reviewText flows to language, sentiment, ambiguity, entities)
  { from: 'reviewText', to: 'detectLanguage', type: 'input' },
  { from: 'reviewText', to: 'analyzeSentiment', type: 'input' },
  { from: 'reviewText', to: 'analyzeAmbiguity', type: 'input' },
  { from: 'reviewText', to: 'extractEntities', type: 'input' },

  // businessType flows to multiple processors
  { from: 'businessType', to: 'analyzeAmbiguity', type: 'input' },
  { from: 'businessType', to: 'extractEntities', type: 'input' },
  { from: 'businessType', to: 'getUnifiedContext', type: 'input' },

  // Context inputs flow to calculateContextScore
  { from: 'businessName', to: 'calculateContextScore', type: 'input' },
  { from: 'businessType', to: 'calculateContextScore', type: 'input' },
  { from: 'businessContext', to: 'calculateContextScore', type: 'input' },
  { from: 'customInstructions', to: 'calculateContextScore', type: 'input' },

  // Style inputs
  { from: 'tone', to: 'shouldIncludeEmojis', type: 'input' },
  { from: 'includeEmojis', to: 'shouldIncludeEmojis', type: 'input' },
  { from: 'responseLength', to: 'calculateResponseLength', type: 'input' },
  { from: 'includeBrackets', to: 'calculateBracketLevel', type: 'input' },

  // Mode routing
  { from: 'mode', to: 'modeRouter', type: 'input' },

  // Processing → Processing (internal dependencies)
  { from: 'analyzeSentiment', to: 'shouldIncludeEmojis', type: 'process' },
  { from: 'analyzeSentiment', to: 'calculateResponseLength', type: 'process' },
  { from: 'calculateContextScore', to: 'calculateBracketLevel', type: 'process' },
  { from: 'analyzeAmbiguity', to: 'calculateBracketLevel', type: 'process' },
  { from: 'modeRouter', to: 'calculateBracketLevel', type: 'process' },
  { from: 'getUnifiedContext', to: 'calculateContextScore', type: 'process' },

  // Processing → Output Sections
  { from: 'calculateContextScore', to: 'identitySection', type: 'output' },
  { from: 'getUnifiedContext', to: 'identitySection', type: 'output' },
  { from: 'shouldIncludeEmojis', to: 'voiceSection', type: 'output' },
  { from: 'analyzeSentiment', to: 'responseApproach', type: 'output' },
  { from: 'calculateBracketLevel', to: 'bracketSection', type: 'output' },
  { from: 'analyzeAmbiguity', to: 'bracketSection', type: 'output' },
  { from: 'analyzeSentiment', to: 'fewShotSection', type: 'output' },
  { from: 'extractEntities', to: 'fewShotSection', type: 'output' },
  { from: 'calculateResponseLength', to: 'outputFormat', type: 'output' },
  { from: 'detectLanguage', to: 'outputFormat', type: 'output' },

  // New dependencies for extended modules (V4.4)
  { from: 'businessType', to: 'getIndustryContext', type: 'input' },
  { from: 'getIndustryContext', to: 'identitySection', type: 'output' },
  { from: 'getIndustryContext', to: 'voiceSection', type: 'output' },
  { from: 'filterSlopPhrases', to: 'voiceSection', type: 'output' },
  { from: 'responseLength', to: 'calculateMaxTokens', type: 'input' },
  { from: 'includeBrackets', to: 'calculateMaxTokens', type: 'input' },
  { from: 'reviewText', to: 'calculateMaxTokens', type: 'input' },
  { from: 'calculateMaxTokens', to: 'outputFormat', type: 'output' },
  { from: 'businessType', to: 'getAmbiguityPatterns', type: 'input' },
  { from: 'reviewText', to: 'getAmbiguityPatterns', type: 'input' },
  { from: 'getAmbiguityPatterns', to: 'analyzeAmbiguity', type: 'process' },
  { from: 'getAmbiguityPatterns', to: 'bracketSection', type: 'output' },
];

// ============================================================================
// FINAL OUTPUT - What goes to the LLM
// ============================================================================
export const FINAL_OUTPUT = {
  systemMessage: {
    description: 'The complete system prompt sent to the LLM',
    sections: ['identitySection', 'voiceSection', 'responseApproach', 'bracketSection', 'fewShotSection', 'outputFormat'],
    approximateTokens: '800-1200',
  },
  userMessage: {
    description: 'The user message with the review',
    format: `REVIEW TO RESPOND TO:
"{reviewText}"

Write a {tone} response. {lengthInstruction}`,
    approximateTokens: '50-200',
  },
  maxTokens: {
    description: 'Maximum tokens for the response',
    calculation: 'TOKEN_LIMITS[responseLength].max * (includeBrackets ? 1.3 : 1)',
    range: '120-650',
  },
  metadata: {
    description: 'Tracking data returned with response',
    fields: ['version', 'mode', 'bracketLevel', 'responseLength', 'sentimentHint', 'customizationScore'],
  },
};
